// components/projects/ProjectDetailsModal.js
// "Workflow" modal for legacy project types (those without a TYPE_UI entry in config/workflows.js).
// Visual language matches WorkflowModal so navigating between project types feels coherent.
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import WorkflowRenderer from '../tasks/WorkflowRenderer.js';
import { WorkflowEngine } from '../../services/workflowEngine.js';
import { getWorkflow, getProjectTypeDisplay } from '../../config/workflows';
import { useAlert } from '../common/AppAlert';

const ProjectDetailsModal = ({ isOpen, onClose, project, user, onProjectUpdate, api }) => {
  const { alert, confirm } = useAlert();
  const [projectDetails, setProjectDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [taskStates, setTaskStates] = useState({});
  const [pendingChanges, setPendingChanges] = useState(new Set());
  const [isCommitting, setIsCommitting] = useState(false);
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('');
  const [reportPath, setReportPath] = useState('');
  const [projectName, setProjectName] = useState('');
  const [editMode, setEditMode] = useState(false);
  const canEdit = user.isAdmin && editMode;

  useEffect(() => {
    if (isOpen && project) {
      setLoading(true);
      setPendingChanges(new Set());
      setEditMode(false); // every open starts in view mode

      api.get(`/projects/${project.id}`, user.token)
        .then(data => {
          setProjectDetails(data);
          setNotes(data.project.notes || '');
          setPriority(data.project.priority || '');
          setReportPath(data.project.report_path || '');
          setProjectName(data.project.project_name || '');

          const states = {};
          data.tasks.forEach(task => {
            states[task.task_number] = {
              checked: !!task.is_completed,
              date: task.completed_at ? new Date(task.completed_at).toLocaleDateString() : '',
              user: task.completed_by || '',
              originalState: !!task.is_completed
            };
          });

          if (project.project_type !== 'Characterization') {
            const workflow = getWorkflow(project.project_type);
            if (workflow) {
              workflow.tasks.forEach(workflowTask => {
                if (!states[workflowTask.id]) {
                  states[workflowTask.id] = {
                    checked: false, date: '', user: '', originalState: false
                  };
                }
              });
            }
          }

          setTaskStates(states);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [isOpen, project, user.token, api]);

  const getCurrentCompletedTaskIds = () => {
    return Object.keys(taskStates)
      .filter(taskId => taskStates[taskId]?.checked)
      .map(id => parseInt(id));
  };

  const handleTaskToggle = (taskId) => {
    if (!canEdit) return; // silently no-op in view mode

    const currentCompletedIds = getCurrentCompletedTaskIds();
    const currentState = taskStates[taskId] || {};
    const newState = !currentState.checked;

    const validation = WorkflowEngine.validateTaskChange(
      project.project_type, taskId, newState, currentCompletedIds
    );
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    if (newState) {
      const today = new Date().toLocaleDateString();
      setTaskStates(prev => ({
        ...prev,
        [taskId]: { ...prev[taskId], checked: true, date: today, user: user.displayName }
      }));
    } else {
      setTaskStates(prev => ({
        ...prev,
        [taskId]: { ...prev[taskId], checked: false, date: '', user: '' }
      }));
    }
    setPendingChanges(prev => new Set(prev).add(taskId));
  };

  const hasChanges = () => (
    pendingChanges.size > 0 ||
    notes !== (projectDetails?.project?.notes || '') ||
    priority !== (projectDetails?.project?.priority || '') ||
    reportPath !== (projectDetails?.project?.report_path || '') ||
    (!!projectDetails && projectName !== (projectDetails.project.project_name || ''))
  );

  const commitChanges = async () => {
    if (!canEdit || !hasChanges()) { onClose(); return; }
    setIsCommitting(true);

    try {
      for (const taskNumber of pendingChanges) {
        const currentState = taskStates[taskNumber];
        const originalState = currentState.originalState;
        if (currentState.checked !== originalState) {
          if (currentState.checked) {
            await api.post(`/projects/${project.id}/tasks/${taskNumber}/complete`, {}, user.token);
          } else {
            await api.post(`/projects/${project.id}/tasks/${taskNumber}/uncomplete`, {}, user.token);
          }
        }
      }

      // Name via its dedicated endpoint — same validation rules as create
      if (projectDetails && projectName !== (projectDetails.project.project_name || '')) {
        const trimmed = projectName.trim();
        if (!trimmed) { alert('Project name is required'); setIsCommitting(false); return; }
        if (trimmed.includes(',')) {
          alert('Comma character (,) cannot be used - Please revise Project Name');
          setIsCommitting(false);
          return;
        }
        await api.put(`/projects/${project.id}/name`, { project_name: trimmed }, user.token);
      }

      if (notes !== (projectDetails?.project?.notes || '')
        || priority !== (projectDetails?.project?.priority || '')
        || reportPath !== (projectDetails?.project?.report_path || '')) {
        if (reportPath && reportPath.trim()) {
          try { new URL(reportPath.trim()); }
          catch { alert('Invalid URL format for report path'); setIsCommitting(false); return; }
        }
        await api.put(`/projects/${project.id}/notes`, { notes, priority, report_path: reportPath }, user.token);
      }

      setPendingChanges(new Set());
      onProjectUpdate();
      onClose();
    } catch (error) {
      alert('Error committing changes: ' + error.message);
    } finally {
      setIsCommitting(false);
    }
  };

  const cancelChanges = async () => {
    if (hasChanges() && !(await confirm('You have unsaved changes. Are you sure you want to exit without saving?'))) return;
    setPendingChanges(new Set());
    onClose();
  };

  const archive = async () => {
    if (!user.isAdmin) return;
    const msg = `Are you sure you want to archive project ${project.project_number} - ${project.project_name}?\n\nThis will remove it from the active project list but preserve all data.`;
    if (!(await confirm(msg, 'Archive Project'))) return;
    try {
      await api.put(`/projects/${project.id}/archive`, {}, user.token);
      onProjectUpdate && onProjectUpdate();
      onClose();
      await alert(`Project ${project.project_number} has been archived.`);
    } catch (e) {
      alert('Error archiving project: ' + e.message);
    }
  };

  if (!isOpen || !project) return null;

  return createPortal(
    <div className="modal-overlay">
      <div className={`modal modal-large${editMode ? '' : ' view-mode'}`}>
        <div className="ta-scroll">

          <div className="ta-modal-header">
            <h2>
              {canEdit && projectDetails ? (
                <input
                  className="input ta-name-input"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  title="Project Name"
                />
              ) : (
                project.project_name
              )}
              <span className="meta">{getProjectTypeDisplay(project.project_type)} Workflow</span>
              {hasChanges() && <span className="unsaved-badge">• Unsaved Changes</span>}
            </h2>
            <div className="header-actions">
              {user.isAdmin && !editMode && (
                <button className="btn btn-edit-mode" onClick={() => setEditMode(true)}>Edit</button>
              )}
              <button className="close" onClick={cancelChanges}>×</button>
            </div>
          </div>

          {loading ? (
            <div className="loading-screen">Loading project details...</div>
          ) : (
            <form>
              <WorkflowRenderer
                projectType={project.project_type}
                completedTaskIds={getCurrentCompletedTaskIds()}
                taskStates={taskStates}
                onTaskToggle={handleTaskToggle}
                pendingChanges={pendingChanges}
                isAdmin={user.isAdmin}
              />

              <div className="ta-section">
                <div className="field-row-flex">
                  <div className="field">
                    <label className="field-label field-label-left">Priority</label>
                    <input
                      type="text"
                      className="input input-centered input-narrow"
                      value={priority}
                      onChange={(e) => setPriority(e.target.value.slice(0, 3))}
                      disabled={!canEdit}
                      placeholder="Priority"
                      maxLength="3"
                    />
                  </div>
                  <div className="field flex-1">
                    <label className="field-label field-label-left">Project Notes</label>
                    <textarea
                      className="textarea"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      disabled={!canEdit}
                      placeholder="Enter project notes..."
                    />
                  </div>
                </div>
              </div>

              <div className="ta-section">
                <div className="field">
                  <label className="field-label field-label-left">Report Path</label>
                  <input
                    type="text"
                    className="input"
                    style={{ width: '100%' }}
                    value={reportPath}
                    onChange={(e) => setReportPath(e.target.value)}
                    disabled={!canEdit}
                    placeholder="Paste Report Path here..."
                  />
                </div>
              </div>
            </form>
          )}

          <div className="ta-modal-footer">
            {user.isAdmin && (
              <button type="button" className="btn btn-archive" onClick={archive} disabled={isCommitting}>
                Archive Project
              </button>
            )}
            <div className="footer-right">
              {editMode ? (
                <>
                  <button type="button" className="btn btn-cancel" onClick={cancelChanges} disabled={isCommitting}>
                    Exit Without Saving
                  </button>
                  <button type="button" className="btn btn-create" onClick={commitChanges} disabled={isCommitting}>
                    {isCommitting ? 'Committing...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button type="button" className="btn" onClick={onClose}>Close</button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProjectDetailsModal;
